import { assocPath, defaultTo, get, getOr, isEmpty, find, map, pipe, flatten, uniqBy } from 'lodash/fp';
import {
  any,
  prop,
  propOr,
  either,
  isNil,
  filter,
  identical,
  both,
  pick,
  values,
  ifElse,
  __,
  multiply,
  always,
  reduce,
  when,
  divide,
  unless,
  equals,
  complement,
  path,
  pathOr,
  indexBy,
  map as ramdaMap,
  anyPass,
} from 'ramda';
import { createSelector } from 'reselect';
import moment from 'moment';
import { castArray } from 'lodash';
import { getIsCommissionSplitsEnabled, getIsDailyPayrollEnabled } from '../main/settings/autoPayout/utils';
import { getFeatureFlags, flagNames } from '../../modules/infra/feature-flags';

import { SHOP_TYPE, SHOP_CURRENCY, COUNTRY_CODE_BY_CURRENCY } from '../main/shops/constants';
import { USER_TYPES } from './constants';
import * as customerConfig from '../customer/config';
import config from '../../config';
import { fullName } from '../common/helpers/users';

export const getShop = get('auth.shop');
export const getShopTimezone = get('auth.activeShop.timezone');
export const getCurrency = getOr(SHOP_CURRENCY.usd, 'auth.currency');
export const getPaymentCurrency = ifElse(
  pipe(get('auth.paymentCurrency'), isNil),
  getCurrency,
  get('auth.paymentCurrency')
);
export const getCountryCodeByCurrency = pipe(getCurrency, (currency) => COUNTRY_CODE_BY_CURRENCY[currency]);

export const getShops = getOr([], 'auth.user.shops');
export const activeShop = get('auth.activeShop');
export const getActiveType = get('auth.activeType');
export const isBrandViewActive = pipe(getActiveType, equals('brand'));
export const getShopById = (state, shopId) =>
  pipe(
    getShops,
    find((shop) => shop.id === shopId)
  )(state);

export const getActiveShopOrAnyBrandShopFlag = (flagName) =>
  ifElse(isBrandViewActive, pipe(getShops, any(prop(flagName))), pipe(activeShop, propOr(false, flagName)));

export const activeShopName = pipe(activeShop, get('name'));
export const activeShopAddress = pipe(activeShop, get('address'));
export const activeShopPhoto = pipe(activeShop, get('photos[0].url'));
export const activeShopLogo = pipe(activeShop, get('logo.url'));
export const activeShopMinInterval = pipe(activeShop, get('minInterval'));
export const activeShopCommissionSplits = get('auth.commissionSplits');
export const activeShopDailyPayrollEnabled = pipe(activeShop, getIsDailyPayrollEnabled);
export const activeShopCommissionSplitsEnabled = pipe(activeShop, getIsCommissionSplitsEnabled);
export const activeShopRewardsEnabled = pipe(
  activeShop,
  ifElse(pipe(get('brand'), isNil), get('rewardsEnabled'), get('brand.rewardsEnabled'))
);
export const activeShopWaitingListEnabled = pipe(activeShop, get('waitingListEnabled'));
export const activeShopKioskEnabled = pipe(activeShop, get('kioskEnabled'));
export const activeShopDefaultCashDrawerBalance = pipe(activeShop, get('defaultCashDrawerBalance'));
export const activeShopReaderlessPos = pipe(activeShop, get('readerlessPos'));
export const activeShopWebreader = pipe(activeShop, both(get('webReader'), get('readerLocationId')));
export const activeShopReaderLocationId = pipe(activeShop, get('readerLocationId'));
export const activeShopNoDefaultTip = pipe(activeShop, get('noDefaultTip'));
export const activeShopWalkInOnly = pipe(activeShop, get('walkInOnly'));
export const activeShopSplitPaymentsEnabled = pipe(activeShop, get('splitPaymentsEnabled'));
export const activeShopGiftCardTipsEnabled = getActiveShopOrAnyBrandShopFlag('giftCardTipsEnabled');
export const activeShopCashTipsEnabled = getActiveShopOrAnyBrandShopFlag('cashTipsEnabled');
export const activeShopReaderTipsEnabled = pipe(activeShop, get('readerTipsEnabled'));
export const activeShopPermissionPassscodeEnabled = pipe(activeShop, get('permissionPasscodeEnabled'));
export const activeShopQuickBooksEnabled = pipe(activeShop, get('useQuickBooks'));
export const activeShopRoute = pipe(activeShop, get('route'));
export const activeShopIsOnlyShopInBrand = pipe(activeShop, get('brand.shopCount'), (count) => count === 1);
export const activeShopDiscountSplit = pipe(activeShop, get('settings.payoutDiscountSplit'));
export const activeShopEmail = pipe(activeShop, get('email'));
export const activeShopCashRegisterCountBillsDefault = pipe(
  activeShop,
  getOr(false, 'settings.cashRegisterCountBillsDefault')
);
export const isApiReaderEnabledForShop = pipe(activeShop, getOr(false, 'settings.apiReader'));

export const activeShopPaymentDepositsEnabled = (shop) => {
  const paymentDepositsEnabled = getFeatureFlags()[flagNames.PAYMENT_DEPOSITS_ENABLED];

  return paymentDepositsEnabled && pipe(activeShop, getOr(true, 'settings.paymentDepositsEnabled'))(shop);
};

export const activeShopNameForBarber = get('auth.barberUser.shop.name');
export const activeShopAddressForBarber = get('auth.barberUser.shop.address');
export const activeShopPhotoForBarber = get('auth.barberUser.shop.photos[0].url');
export const activeShopLogoForBarber = get('auth.barberUser.shop.logo.url');
export const barberActiveShop = get('auth.barberUser.shop');
export const barberActiveShopId = get('auth.barberUser.shop.id');
export const getUserRole = get('auth.user.kind');
export const getShopKind = get('auth.shopKind');
export const getAdminUser = get('auth.adminUser');
export const getUserPermissions = getOr([], 'auth.user.permissions');
export const hasBarberBankAccount = pipe(get('auth.barberUser.bankAccountId'), Boolean);
export const hasShopBankAccount = pipe(get('auth.activeShop.bankAccountId'), Boolean);
export const isBarberUser = (state) => getUserRole(state) === USER_TYPES.barber;

const getIsAdminImpersonated = pipe(getAdminUser, Boolean);

export const getIsRegularAdmin = pipe(getUserRole, identical(USER_TYPES.regularAdmin));
export const getIsSuperAdmin = pipe(getUserRole, identical(USER_TYPES.admin));
export const getIsAgent = pipe(getUserRole, identical(USER_TYPES.agent));

export const getIsAdminRole = anyPass([getIsRegularAdmin, getIsSuperAdmin, getIsAgent]);
export const getIsAdminUser = (state) => either(getIsAdminRole, getIsAdminImpersonated)(state);

export const getIsRentalShop = pipe(getShopKind, identical(SHOP_TYPE.rental));
export const getIsCommissionShop = pipe(getShopKind, identical(SHOP_TYPE.commission));

export const getIsRentalBarber = both(isBarberUser, getIsRentalShop);
export const getIsCommissionBarber = both(isBarberUser, getIsCommissionShop);

export const getIsShop = pipe(getUserRole, identical(USER_TYPES.shop));

export const getIsShopUserOrRentalBarberUser = either(getIsShop, getIsRentalShop);

export const getActiveShop = (state) => activeShop(state) || barberActiveShop(state);
export const getActiveShopId = pipe(getActiveShop, get('id'));
export const getActiveShopCurrency = pipe(getActiveShop, get('currency'));

export const getActiveShopProductSalesToShop = pipe(getActiveShop, get('productSalesToShop'));

export const getActiveShopPaymentProcessorReader = pipe(getActiveShop, get('paymentProcessorReader'));

export const getActiveShopCountry = pipe(getActiveShop, pathOr(null, ['address', 'country']));

export const getBarbers = get('auth.user.barbers');
const mapShops = map('shop');
const addBarberId = assocPath('shop.barberId');
export const getActiveBarberId = get('auth.barberUser.id');
export const getActiveBarberName = createSelector(get('auth.barberUser'), (barber) => fullName(barber));
export const getBarberShops = pipe(
  getBarbers,
  map((barber) => addBarberId(barber.id, barber)),
  mapShops
);
export const getBrandId = get('auth.user.brand.id');
export const getActiveBrandId = createSelector(getBrandId, getActiveType, (brandId, activeType) => {
  if (activeType === 'brand') {
    return brandId;
  }

  return null;
});
export const getRoleId = (state) => {
  if (getActiveBrandId(state)) {
    return getActiveBrandId(state);
  }
  return state.auth.barberUser ? state.auth.barberUser.id : state.auth.shop;
};
export const getUserType = (state) => (state.auth.barberUser ? 'barber' : 'shop');
export const getRoleIdType = createSelector([getRoleId, getActiveType], (id, type) => ({
  id,
  type,
}));

export const getShopOrBarberField = (state, field) => {
  if (state.auth.barberUser) {
    return get(field)(state.auth.barberUser);
  }

  return pipe(getActiveShop, get(field))(state);
};

export const getShopIdOrBarberUser = (state) => {
  if (state.auth.barberUser) return state.auth.barberUser;
  return state.auth.shop;
};

export const getTimezone = (state) => state.auth.timezone;
export const getDateFormat = pipe(
  ifElse(getIsAdminUser, () => config.dateFormat, path(['auth', 'dateFormat'])),
  defaultTo(config.dateFormat)
);
export const getBarberUser = (state) => state.auth.barberUser;
export const getBarberUserId = get('auth.barberUser.id');

export const getFirstLogin = get('auth.user.isFirstLogin');

export const getFrontendFlags = pipe(get('auth.user.frontendFlags'), defaultTo({}));

export const getEngagementOnboarded = get('auth.user.frontendFlags.engagementOnboarded');

export const getMaintenanceMessage = pipe(get('auth.user.frontendFlags.maintenance.message'), defaultTo(null));
export const isMaintenanceDismissed = pipe(get('auth.user.maintenanceDismissed'), defaultTo(false));

const hasPermissions = (object, operation) =>
  pipe(
    getUserPermissions,
    filter((permission) => permission.object === object && permission.operation === operation),
    complement(isEmpty)
  );

export const getIsReadOnlyAdminUser = both(
  getIsRegularAdmin,
  pipe(hasPermissions('admin', 'modify'), identical(false))
);

const NORTH_END_SHOP = 'fb498f5d-0936-4761-8202-6d73a270c87f';
const isNorthEndShop = pipe(get('id'), identical(NORTH_END_SHOP));
const useNorthEndTips = always(customerConfig.tipsNorthEnd);

const anyTierIsZero = pipe(reduce(multiply, 1), identical(0));
const formatLabel = (tier) => `${Math.round(tier * 100)}%`;
const mapTier = (tier) => ({
  value: tier,
  label: formatLabel(tier),
});

const useDefaultTips = always(customerConfig.defaultTips);
const shopTiers = pipe(pick(['tipTier1', 'tipTier2', 'tipTier3', 'tipTier4']), values, map(divide(__, 100)));
const useShopTiers = unless(isEmpty, shopTiers);

export const getShopTipTiers = pipe(
  getActiveShop,
  ifElse(isNorthEndShop, useNorthEndTips, useShopTiers),
  when(either(isEmpty, anyTierIsZero), useDefaultTips),
  map(mapTier)
);

export const canUseGiftCards = getActiveShopOrAnyBrandShopFlag('giftCardEnabled');

export const isUserOfRole = (state, roleOrArrayOfRoles) => {
  const activeRole = getUserRole(state);
  const rolesToCheck = castArray(roleOrArrayOfRoles);
  return rolesToCheck.includes(activeRole);
};

export const canSellGiftCards = (state) => {
  const isShopUser = isUserOfRole(state, USER_TYPES.shop);
  const isRentalBarber = getIsRentalBarber(state);
  return canUseGiftCards(state) && (isShopUser || isRentalBarber);
};

export const canUsePasscode = pipe(activeShop, get('bookingCodeEnabled'));

export const getActiveShopUserPermissionPasscode = getOr('', 'auth.permissionPasscode');
export const getIsPermissionPasscodeRequired = getOr(false, 'auth.permissionPasscodeRequired');

export const getActiveBrandName = get('auth.user.brand.name');
export const getBrand = (state) => get('auth.user.brand', state);
export const getToken = get('auth.token');
export const getCurrentUserId = get('auth.user.id');
export const getCurrentUserUsername = get('auth.user.username');
export const getShopBrand = get('auth.shopBrand');
export const getShopBrandId = pipe(getShopBrand, get('id'));
export const getShopBrandSmsOriginator = pipe(getShopBrand, get('smsOriginator'));
export const getShopBrandSmsOriginatorVerified = pipe(getShopBrand, get('smsOriginatorVerified'));
export const getBrandSmsOriginator = pipe(getShopBrand, get('smsOriginator'));
export const getActiveShopBrandId = createSelector(getActiveShop, getShopBrandId, (shop, shopBrandId) =>
  shop ? shop.brandId : shopBrandId
);

export const getActiveBrandShops = createSelector(getShops, getActiveType, (shops, activeType) => {
  if (activeType === 'brand') {
    return shops;
  }

  return null;
});

export const getAllBarbers = (state) => createSelector(getShops, pipe(map('barbers'), flatten, uniqBy('id')))(state);

export const getShopBarbers = (state, shopId) =>
  pipe(
    getShops,
    find((shop) => shop.id === shopId),
    getOr([], 'barbers')
  )(state);

export const getShopNamesById = pipe(getShops, indexBy(prop('id')), ramdaMap(prop('name')));

export const brandRewardsEnabled = createSelector(get('auth.user.brand'), getActiveType, (brand, activeType) => {
  if (activeType === 'brand') {
    return brand.rewardsEnabled;
  }

  return false;
});

export const isTipOnProductEnabled = pipe(getActiveShop, getOr(false, 'tipOnProduct'));

export const getUserUrlPrefix = (state) => {
  const type = getActiveType(state);
  if (type === 'shop') {
    return '';
  }

  return `/${type}`;
};

export const getShopOrBrandIdAndType = (state) => {
  const type = getActiveType(state);
  if (type === 'shop') {
    return { id: getShop(state), type };
  }

  return { id: getActiveBrandId(state), type };
};

export const isBrandShop = (state) => {
  const type = getActiveType(state);
  const shop = getActiveShop(state);

  return type === 'shop' && shop.brandId;
};

export const getIsSupplyBannerVisible = createSelector(getActiveShop, getIsAdminUser, (shop, isAdmin) => {
  if (!shop || isAdmin) {
    return false;
  }

  const { timezone, frontendFlags } = shop;
  const { supplyCounter: counter } = frontendFlags;
  if (!counter) {
    return true;
  }

  const isSameDay = moment().tz(timezone).isSame(moment(counter.latest).tz(timezone), 'day');
  return counter.count < 7 && !isSameDay;
});

export const getOwnerTypeAndId = (state) => {
  let ownerId = getShop(state);
  let ownerType = 'shop';

  const barber = getBarberUser(state);
  if (barber) {
    ownerId = barber.id;
    ownerType = 'barber';
  }

  const brandId = getActiveBrandId(state);
  if (brandId) {
    ownerId = brandId;
    ownerType = 'brand';
  }

  return {
    id: ownerId,
    type: ownerType,
  };
};

const canShopUseMemberships = pipe(pathOr(false, ['settings', 'memberships']), equals(true));

const canBrandUseMemberships = getOr(false, 'settings.membershipsEnabled');

export const canActiveShopUseMemberships = pipe(getActiveShop, canShopUseMemberships);

export const canActiveBrandUseMemberships = pipe(getShopBrand, canBrandUseMemberships);

export const isShopUserPermissionPasscodeLoading = get('auth.permissionPasscodeLoading');

export const getBrandSingleShopId = pipe(getActiveBrandShops, get('0.id'));

export const getBrandSingleShopName = pipe(getActiveBrandShops, get('0.name'));

export const isSingleShopBrand = pipe(getBrand, get('shopCount'), equals(1));

export const isSingleShopOtpBrand = both(
  isSingleShopBrand,
  pipe(getActiveBrandShops, getOr(false, '0.commissionSplits'))
);

export const getIsIndyBarber = (state) =>
  getIsRentalBarber(state) && getShopBrand(state).shopCount === 1 && getActiveShop(state).barberCount === 1;
